/**
 * Task statuses — read model for the settings-task-statuses feature.
 *
 * Tasks carry a fixed status enum; per status the org authors a markdown
 * instruction (Layer 3 of the agent's layered context for tasks) plus a
 * one-line plain-text description. The 6 default `task_status_instructions`
 * rows (`role_id` NULL) are seeded on org creation — turn 11.2a — so this
 * surface never sees an "empty" state. Turn 11.2b ships the model + VM + the
 * six-expandable-row view shell + the Edit-tab working copy; per-role
 * overrides land in 11.2c, the live merged preview in 11.2d, Save in 11.2e,
 * History in 11.2f.
 */

/** Soft length warning threshold on the instruction markdown — no hard cap. */
export const TASK_STATUS_SOFT_WARN = 4000;

/** Max length of the per-status `description` (enforced server-side too). */
export const TASK_STATUS_DESCRIPTION_MAX = 200;

/** The 6 fixed task status keys, in canonical display order. */
export type TaskStatusKey =
  | 'todo'
  | 'in_progress'
  | 'blocked'
  | 'in_review'
  | 'done'
  | 'cancelled';

export const TASK_STATUS_KEYS: readonly TaskStatusKey[] = [
  'todo',
  'in_progress',
  'blocked',
  'in_review',
  'done',
  'cancelled'
];

/** Human display label for each status key (the enum value is snake_case). */
export const TASK_STATUS_LABELS: Record<TaskStatusKey, string> = {
  todo: 'todo',
  in_progress: 'in progress',
  blocked: 'blocked',
  in_review: 'in review',
  done: 'done',
  cancelled: 'cancelled'
};

export function isTaskStatusKey(value: string): value is TaskStatusKey {
  return (TASK_STATUS_KEYS as readonly string[]).includes(value);
}

/** The seeded `description` + `instruction_md` for a default status row. */
export interface TaskStatusDefault {
  description: string;
  instructionMd: string;
}

/**
 * Platform pre-seeded defaults — the exact strings turn 11.2a seeds into every
 * org's 6 default `task_status_instructions` rows (and the same ones baked into
 * the settings-task-statuses mock). Used to resolve the `customized` badge: a
 * row whose content still equals its seed has never been edited.
 */
export const TASK_STATUS_SEED_DEFAULTS: Record<
  TaskStatusKey,
  TaskStatusDefault
> = {
  todo: {
    description: 'Not started; in the backlog.',
    instructionMd:
      'Confirm scope before starting. Surface uncertainty about acceptance criteria or references.'
  },
  in_progress: {
    description: 'Active work.',
    instructionMd:
      'Append progress notes; do not rewrite earlier context. Keep description in sync with the actual change.'
  },
  blocked: {
    description: 'Stalled; blocker in description or references.',
    instructionMd:
      'Capture the blocker explicitly (linked PR / waiting on reference / decision needed). No work continues here until cleared.'
  },
  in_review: {
    description: 'Author is done; needs review.',
    instructionMd:
      'Verify references are current. Address review comments inline; do not silently reinterpret.'
  },
  done: {
    description: 'Completed.',
    instructionMd:
      'Confirm a QA sign-off doc reference if your org uses one. Append a one-line summary of what shipped.'
  },
  cancelled: {
    description: "Won't fix / no longer relevant.",
    instructionMd:
      'Note the reason. Preserve any partial work for re-opening later.'
  }
};

/**
 * One default `task_status_instructions` row — the `role_id` NULL row for a
 * status. Per-role override rows (`role_id` set) are loaded by the VM in 11.2c;
 * this turn loads default rows only.
 */
export interface TaskStatusInstruction {
  /** `task_status_instructions.id`. Empty string only if the seed is missing. */
  id: string;
  orgId: string;
  status: TaskStatusKey;
  /** Always null on a default row — kept for the 11.2c override join. */
  roleId: string | null;
  /** One-line plain-text gloss. */
  description: string;
  /** Markdown instruction body. */
  instructionMd: string;
  /** Soft-delete — null on default rows in practice. */
  archivedAt: string | null;
  /** ISO timestamp; display-only. */
  updatedAt: string;
}

/**
 * One immutable `task_status_instruction_versions` snapshot — minted on every
 * Save / Restore of a `(status, role)` cell. The version table denormalizes
 * `status` + `roleId` so the History overlay can interleave the default row's
 * versions with each role override's versions for a status without a join.
 */
export interface TaskStatusInstructionVersion {
  id: string;
  /** The `task_status_instructions` row this snapshot belongs to. */
  instructionId: string;
  status: TaskStatusKey;
  /** Null when the snapshot is of the status' default row, set for an override. */
  roleId: string | null;
  versionNumber: number;
  description: string;
  instructionMd: string;
  /** The version this snapshot was restored from, when it was minted by a
   *  Restore. Null for an ordinary Save snapshot. */
  restoredFromVersionId: string | null;
  createdAt: string;
  createdBy: string | null;
}

/** Newest-first — by `createdAt` desc. `versionNumber` is per-instruction, so
 *  across a status' interleaved default + override snapshots only the timestamp
 *  gives a stable global order. */
export function sortTaskStatusVersions(
  versions: readonly TaskStatusInstructionVersion[]
): TaskStatusInstructionVersion[] {
  return [...versions].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
  );
}

/**
 * Synthesizes a default row from the platform seed — a defensive fallback when
 * a status' seeded row is somehow absent (it should always exist post-11.2a).
 */
export function seedRow(
  orgId: string,
  status: TaskStatusKey
): TaskStatusInstruction {
  const seed = TASK_STATUS_SEED_DEFAULTS[status];
  return {
    id: '',
    orgId,
    status,
    roleId: null,
    description: seed.description,
    instructionMd: seed.instructionMd,
    archivedAt: null,
    updatedAt: new Date(0).toISOString()
  };
}

/**
 * True when the status' content diverges from its platform seed — drives the
 * `customized` badge on a collapsed row. Compares whatever the editor is
 * currently showing (the working copy when dirty, else the saved row).
 */
export function isCustomized(value: {
  status: TaskStatusKey;
  description: string;
  instructionMd: string;
}): boolean {
  const seed = TASK_STATUS_SEED_DEFAULTS[value.status];
  return (
    value.description !== seed.description ||
    value.instructionMd !== seed.instructionMd
  );
}
